import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
    Box,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress,
    Alert,
    AlertTitle,
    Pagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Tabs,
    Tab,
    Stack,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Divider
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    Visibility as ViewIcon,
    CheckCircle as ApproveIcon,
    Cancel as RejectIcon,
    Download as DownloadIcon,
    Publish as PublishIcon,
    Settings as SettingsIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Image as ImageIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axiosWithAuth from '../utils/axiosWithAuth';
import RewrittenContentViewer from '../components/RewrittenContent/RewrittenContentViewer';
import {
    getContentForReview,
    updateContentReview,
    bulkReviewContent,
    bulkRejectContent,
    bulkDownloadContent,
    bulkPublishContent,
    fetchUserProperties,
    createExternalBlog,
    createExternalPage,
    createExternalImageGeneration,
    getWordPressConfig,
    createWordPressConfig,
    updateWordPressConfig,
    getBusinessProfile,
    updateBusinessProfile,
    fetchAvailableGoogleProperties,
    createManagedProperty,
    fetchManagedPropertyById,
    updateManagedProperty
} from '../services/casService';
import { getAvailableImages, bulkGenerateImages, generateImageForContent } from '../services/imageGenerationService';

const CASDashboardPage = () => {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [content, setContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState('');
    const [selectedRows, setSelectedRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sortBy, setSortBy] = useState('created_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [pageSize] = useState(20);
    const [availableImages, setAvailableImages] = useState([]);

    // Modal states
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedContent, setSelectedContent] = useState(null);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [reviewNotes, setReviewNotes] = useState('');
    const [rejectReason, setRejectReason] = useState('');

    // Additional modal states for top action buttons
    const [externalContentModalOpen, setExternalContentModalOpen] = useState(false);
    const [propertyProfileModalOpen, setPropertyProfileModalOpen] = useState(false);
    const [propertySettingsModalOpen, setPropertySettingsModalOpen] = useState(false);
    const [propertyDetailsModalOpen, setPropertyDetailsModalOpen] = useState(false);


    // Form states for modals
    const [externalContentForm, setExternalContentForm] = useState({
        activeTab: 'blog',
        // Blog form state
        blogTopics: '',
        blogTargetAudience: 'general web users',
        blogDesiredTone: 'informative and engaging',
        blogQualityLevel: 'medium',
        // Page form state
        pageName: '',
        pagePurpose: '',
        pageDescription: '',
        pageTargetAudience: 'general web users',
        pageDesiredTone: 'informative and engaging',
        pageQualityLevel: 'medium',
        // Image Generation form state
        imagePrompts: '',
        imageTargetAudience: 'general web users',
        imageDesiredTone: 'professional and engaging',
        imageQualityLevel: 'medium'
    });

    const [propertyProfile, setPropertyProfile] = useState(null);
    const [editedPropertyProfile, setEditedPropertyProfile] = useState(null);
    const [activeProfileSection, setActiveProfileSection] = useState('business_summary');
    const [isProfileLoading, setIsProfileLoading] = useState(false);
    const [isProfileSaving, setIsProfileSaving] = useState(false);
    const [profileError, setProfileError] = useState('');
    const [showProfileSaveModal, setShowProfileSaveModal] = useState(false);

    const [propertySettingsForm, setPropertySettingsForm] = useState({
        friendly_name: '',
        is_active_for_cycles: true,
        site_url: '',
        username: '',
        password: '',
        is_enabled: false,
        auto_publish: false,
        default_status: 'publish',
        default_category: '',
        default_tags: [],
        featured_image_enabled: true,
        seo_meta_enabled: true,
        auto_slug_generation: true
    });

    // Property Settings Modal states
    const [property, setProperty] = useState(null);
    const [wordpressConfig, setWordpressConfig] = useState(null);
    const [isSettingsLoading, setIsSettingsLoading] = useState(false);
    const [isSettingsSaving, setIsSettingsSaving] = useState(false);
    const [settingsError, setSettingsError] = useState('');
    const [activeSettingsTab, setActiveSettingsTab] = useState('property');
    const [isWordPressOnlyMode, setIsWordPressOnlyMode] = useState(false);

    useEffect(() => {
        loadUserProperties();
    }, []);

    // Handle URL parameters for subscription selection
    useEffect(() => {
        const subscriptionId = searchParams.get('subscriptionId');
        if (subscriptionId && properties.length > 0) {
            // Find the property associated with this subscription
            const property = properties.find(prop => prop.subscription_id === parseInt(subscriptionId));
            if (property) {
                setSelectedProperty(property.id);
                // Clear the subscriptionId parameter from URL
                setSearchParams({});
            }
        }
    }, [searchParams, properties]);

    // Check authentication status
    const checkAuthStatus = () => {
        const token = localStorage.getItem('token');
        if (!token) {
            setError('Authentication required. Please log in again.');
            return false;
        }
        return true;
    };

    const fetchContent = async () => {
        try {
            // Don't fetch content if no property is selected
            if (!selectedProperty) {
                setContent([]);
                setTotalPages(1);
                return;
            }

            setLoading(true);
            const params = {
                page: currentPage,
                size: pageSize,
                sort_by: sortBy,
                sort_order: sortOrder,
            };

            const response = await getContentForReview(selectedProperty, params);
            setContent(response.content_items || []);
            setTotalPages(response.pagination?.total_pages || 1);
        } catch (error) {
            console.error('Error fetching content:', error);
            setError('Failed to fetch content for review');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (selectedProperty) {
            fetchContent();
        }
    }, [selectedProperty, currentPage, sortBy, sortOrder]);

    // Fetch available images after content is loaded
    useEffect(() => {
        if (content.length > 0) {
            fetchAvailableImages();
        }
    }, [content]);

    // Listen for refreshContent events from the content viewer
    useEffect(() => {
        const handleRefreshContent = () => {
            fetchContent();
        };

        window.addEventListener('refreshContent', handleRefreshContent);

        return () => {
            window.removeEventListener('refreshContent', handleRefreshContent);
        };
    }, []);

    const loadUserProperties = async () => {
        try {
            setLoading(true);
            // Get user's assigned CAS properties
            const propertiesData = await fetchUserProperties();



            setProperties(propertiesData || []);

            // Auto-select the first property if available and no property is currently selected
            if (propertiesData && propertiesData.length > 0 && !selectedProperty) {
                setSelectedProperty(propertiesData[0].id);
            }
        } catch (error) {
            console.error('Error fetching user properties:', error);
            setError('Failed to fetch your properties');
            setProperties([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableImages = async () => {
        try {
            const availableImageIds = await getAvailableImages();

            // Filter available images to only include images for current subscription's content
            const currentContentIds = content.map(item => item.id);
            const filteredAvailableImages = availableImageIds.filter(id => currentContentIds.includes(id));


            setAvailableImages(filteredAvailableImages);
        } catch (error) {
            console.error('Error fetching available images:', error);
            setAvailableImages([]);
        }
    };


    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await fetchContent();
            await fetchAvailableImages();
            setSuccess('Content refreshed successfully');
        } catch (error) {
            setError('Failed to refresh content');
        } finally {
            setIsRefreshing(false);
        }
    };

    const handlePropertyChange = (propertyId) => {
        setSelectedProperty(propertyId);
        setCurrentPage(1);
        setSelectedRows([]);
    };

    const handleSortChange = (sortBy) => {
        setSortBy(sortBy);
        setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSelectRow = (item) => {
        setSelectedRows(prev =>
            prev.find(row => row.id === item.id)
                ? prev.filter(row => row.id !== item.id)
                : [...prev, item]
        );
    };

    const handleSelectAll = () => {
        if (selectedRows.length === content.length) {
            setSelectedRows([]);
        } else {
            setSelectedRows([...content]);
        }
    };

    const handleBulkReview = async () => {
        if (selectedRows.length === 0) {
            setError('Please select content to review');
            return;
        }

        try {
            const contentIds = selectedRows.map(row => row.id);
            await bulkReviewContent(contentIds, reviewNotes);

            setSuccess('Content reviewed successfully');
            setSelectedRows([]);
            setReviewNotes('');
            setReviewModalOpen(false);
            fetchContent();
        } catch (error) {
            console.error('Error bulk reviewing content:', error);
            setError('Failed to review content');
        }
    };

    const handleBulkReject = async () => {
        if (selectedRows.length === 0) {
            setError('Please select content to reject');
            return;
        }

        try {
            const contentIds = selectedRows.map(row => row.id);
            await bulkRejectContent(contentIds, rejectReason);

            setSuccess('Content rejected successfully');
            setSelectedRows([]);
            setRejectReason('');
            setRejectModalOpen(false);
            fetchContent();
        } catch (error) {
            console.error('Error bulk rejecting content:', error);
            setError('Failed to reject content');
        }
    };

    const handleBulkPublish = async () => {
        if (!checkAuthStatus()) return;

        if (selectedRows.length === 0) {
            setError('Please select content to publish');
            return;
        }

        try {

            const contentIds = selectedRows.map(row => row.id);
            const result = await bulkPublishContent(contentIds, selectedProperty, 'wordpress');


            setSuccess('Content published successfully');
            setSelectedRows([]);
            fetchContent();
        } catch (error) {
            console.error('Error bulk publishing content:', error);
            setError(`Failed to publish content: ${error.message}`);
        }
    };

    const handleBulkDownload = async () => {
        if (selectedRows.length === 0) {
            setError('Please select content to download');
            return;
        }

        try {

            const contentIds = selectedRows.map(row => row.id);
            const blob = await bulkDownloadContent(contentIds, 'txt');


            if (!blob || blob.size === 0) {
                throw new Error('Received empty or invalid blob');
            }

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `content_${Date.now()}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setSuccess('Content downloaded successfully');
            setSelectedRows([]);
        } catch (error) {
            console.error('Error bulk downloading content:', error);
            setError(`Failed to download content: ${error.message}`);
        }
    };

    const handleReview = async (item) => {
        setSelectedContent(item);
        setReviewModalOpen(true);
    };

    const handleReject = async (item) => {
        setSelectedContent(item);
        setRejectModalOpen(true);
    };

    const handleDownload = async (item) => {
        try {
            const blob = await bulkDownloadContent([item.id], 'txt');

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `content_${item.id}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setSuccess('Content downloaded successfully');
        } catch (error) {
            console.error('Error downloading content:', error);
            setError('Failed to download content');
        }
    };

    const handlePublish = async (item) => {
        if (!checkAuthStatus()) return;

        try {

            const result = await bulkPublishContent([item.id], selectedProperty, 'wordpress');

            setSuccess('Content published successfully');
            fetchContent(); // Refresh the content list
        } catch (error) {
            console.error('Error publishing content:', error);
            if (error.response && error.response.status === 401) {
                setError('Authentication failed. Please log in again.');
            } else {
                setError(`Failed to publish content: ${error.message || 'Unknown error'}`);
            }
        }
    };

    const handleView = (item) => {
        setSelectedContent(item);
        setViewModalOpen(true);
    };

    const handleSingleReview = async () => {
        try {
            await axiosWithAuth.put(`/cas/content-review/${selectedProperty}/content/${selectedContent.id}`, {
                review_status: 'reviewed',
                review_notes: reviewNotes
            });

            setSuccess('Content reviewed successfully');
            setReviewModalOpen(false);
            setReviewNotes('');
            setSelectedContent(null);
            fetchContent();
        } catch (error) {
            console.error('Error reviewing content:', error);
            setError('Failed to review content');
        }
    };

    const handleSingleReject = async () => {
        try {
            await axiosWithAuth.put(`/cas/content-review/${selectedProperty}/content/${selectedContent.id}`, {
                review_status: 'rejected',
                review_notes: rejectReason
            });

            setSuccess('Content rejected successfully');
            setRejectModalOpen(false);
            setRejectReason('');
            setSelectedContent(null);
            fetchContent();
        } catch (error) {
            console.error('Error rejecting content:', error);
            setError('Failed to reject content');
        }
    };

    // Mobile-friendly content card component
    const ContentCard = ({ item }) => (
        <Card sx={{ mb: 2, maxWidth: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Header with checkbox and title */}
                <Box display="flex" alignItems="flex-start" mb={2} gap={1}>
                    <Checkbox
                        checked={selectedRows.some(row => row.id === item.id)}
                        onChange={() => handleSelectRow(item)}
                        sx={{ mt: -0.5 }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {item.rewritten_title || item.original_title || item.title || 'Untitled Content'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {item.rewritten_meta_description || item.original_meta_description || 'No description'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                            ID: {item.id}
                        </Typography>
                    </Box>
                </Box>

                {/* Status and Type badges */}
                <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                    {getContentTypeBadge(item.content_type || 'unknown')}
                    {getStatusBadge(item.status || 'pending')}
                </Box>

                <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Type</Typography>
                        <Typography variant="body2">
                            {item.content_type || 'Unknown'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="body2" color="textSecondary">Created</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {item.created_at ? format(new Date(item.created_at), 'MMM dd, yyyy') : 'N/A'}
                        </Typography>
                    </Grid>
                </Grid>

                {item.summary && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">Summary</Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word', overflow: 'hidden' }}>
                            {item.summary.length > 100 ? `${item.summary.substring(0, 100)}...` : item.summary}
                        </Typography>
                    </Box>
                )}

                <Divider sx={{ my: 1 }} />

                <Box display="flex" gap={1} flexWrap="wrap" justifyContent="center" sx={{ maxWidth: '100%' }}>
                    <Tooltip title="View Content">
                        <IconButton
                            size="medium"
                            onClick={() => handleView(item)}
                        >
                            <ViewIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Approve">
                        <IconButton
                            size="medium"
                            color="success"
                            onClick={() => handleReview(item)}
                        >
                            <ApproveIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject">
                        <IconButton
                            size="medium"
                            color="error"
                            onClick={() => handleReject(item)}
                        >
                            <RejectIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                        <IconButton
                            size="medium"
                            color="primary"
                            onClick={() => handleDownload(item)}
                        >
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Publish">
                        <IconButton
                            size="medium"
                            color="secondary"
                            onClick={() => handlePublish(item)}
                        >
                            <PublishIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </CardContent>
        </Card>
    );

    const handleBulkGenerateImages = async () => {
        if (selectedRows.length === 0) {
            setError('Please select content to generate images for');
            return;
        }
        try {
            const contentIds = selectedRows.map(row => row.id);
            await bulkGenerateImages(contentIds);
            setSuccess('Image generation initiated for selected items');
            setSelectedRows([]);
            fetchContent();
        } catch (error) {
            console.error('Error bulk generating images:', error);
            setError('Failed to bulk generate images');
        }
    };

    const handleGenerateImage = async (item) => {
        try {
            await generateImageForContent(item.id);
            setSuccess('Image generation initiated');
            fetchContent();
        } catch (error) {
            console.error('Error generating image:', error);
            setError('Failed to generate image');
        }
    };

    // Top action button handlers
    const handleCreateExternalContent = async () => {
        try {
            if (externalContentForm.activeTab === 'blog') {
                const topics = externalContentForm.blogTopics.split('\n').filter(topic => topic.trim());
                if (topics.length === 0) {
                    setError('Please enter at least one blog topic');
                    return;
                }

                await createExternalBlog({
                    managed_property_id: selectedProperty,
                    blog_topics: topics,
                    target_audience: externalContentForm.blogTargetAudience,
                    desired_tone: externalContentForm.blogDesiredTone,
                    quality_level: externalContentForm.blogQualityLevel
                });
                setSuccess('Blog analysis items created successfully!');
            } else if (externalContentForm.activeTab === 'page') {
                if (!externalContentForm.pageName.trim() || !externalContentForm.pagePurpose.trim() || !externalContentForm.pageDescription.trim()) {
                    setError('Please fill in all required fields');
                    return;
                }

                await createExternalPage({
                    managed_property_id: selectedProperty,
                    page_name: externalContentForm.pageName.trim(),
                    page_purpose: externalContentForm.pagePurpose.trim(),
                    page_description: externalContentForm.pageDescription.trim(),
                    target_audience: externalContentForm.pageTargetAudience,
                    desired_tone: externalContentForm.pageDesiredTone,
                    quality_level: externalContentForm.pageQualityLevel
                });
                setSuccess('Page analysis item created successfully!');
            } else if (externalContentForm.activeTab === 'image') {
                const prompts = externalContentForm.imagePrompts.split('\n').map(p => p.trim()).filter(p => p.length > 0);
                if (prompts.length === 0) {
                    setError('Please enter at least one image prompt');
                    return;
                }

                await createExternalImageGeneration({
                    managed_property_id: selectedProperty,
                    blog_topics: prompts,  // Reuses blog_topics field for image prompts
                    target_audience: externalContentForm.imageTargetAudience,
                    desired_tone: externalContentForm.imageDesiredTone,
                    quality_level: externalContentForm.imageQualityLevel
                });
                setSuccess('Image generation analysis items created successfully!');
            }

            setExternalContentModalOpen(false);
            // Reset form
            setExternalContentForm({
                activeTab: 'blog',
                blogTopics: '',
                blogTargetAudience: 'general web users',
                blogDesiredTone: 'informative and engaging',
                blogQualityLevel: 'medium',
                pageName: '',
                pagePurpose: '',
                pageDescription: '',
                pageTargetAudience: 'general web users',
                pageDesiredTone: 'informative and engaging',
                pageQualityLevel: 'medium',
                imagePrompts: '',
                imageTargetAudience: 'general web users',
                imageDesiredTone: 'professional and engaging',
                imageQualityLevel: 'medium'
            });
            fetchContent(); // Refresh content list
        } catch (error) {
            console.error('Error creating external content:', error);
            setError('Failed to create external content');
        }
    };

    // Profile Editor functions
    const loadPropertyProfile = async () => {
        if (!checkAuthStatus()) return;

        setIsProfileLoading(true);
        setProfileError('');
        try {
            const profileData = await getBusinessProfile(selectedProperty);



            if (!profileData) {
                throw new Error('No profile data received from server');
            }

            // Check if profileData is empty or has unexpected structure
            if (Object.keys(profileData).length === 0) {

                throw new Error('Profile data is empty');
            }

            // Check if the response has a nested structure
            let finalProfileData = profileData;
            if (profileData.data) {

                finalProfileData = profileData.data;
            }

            // Create a default profile structure if data is empty or doesn't exist
            const normalizedProfile = {
                business_summary: {
                    business_name: finalProfileData.business_summary?.business_name || '',
                    executive_summary: finalProfileData.business_summary?.executive_summary || '',
                    core_offerings: finalProfileData.business_summary?.core_offerings || '',
                    business_model: finalProfileData.business_summary?.business_model || '',
                    target_audience: finalProfileData.business_summary?.target_audience || '',
                    market_positioning: finalProfileData.business_summary?.market_positioning || ''
                },
                content_strategy: finalProfileData.content_strategy || {
                    pillar_content_themes: [],
                    thought_leadership_themes: [],
                    faq_content_themes: [],
                    case_study_themes: []
                },
                learning_intelligence: finalProfileData.learning_intelligence || {
                    business_keywords: [],
                    industry_terms: [],
                    irrelevant_patterns: [],
                    search_intent_patterns: {},
                    content_themes: [],
                    topic_categories: [],
                    competitive_keywords: [],
                    audience_search_patterns: [],
                    industry_trends: [],
                    solution_keywords: []
                },
                blog_topics: finalProfileData.blog_topics || {
                    primary_topics: [],
                    secondary_topics: [],
                    seasonal_topics: [],
                    trending_topics: [],
                    evergreen_topics: []
                },
                scraped_urls: finalProfileData.scraped_urls || [],
                distilled_context: finalProfileData.distilled_context || ''
            };

            setPropertyProfile(normalizedProfile);
            setEditedPropertyProfile(JSON.parse(JSON.stringify(normalizedProfile))); // Deep copy
        } catch (err) {
            console.error('Error loading profile:', err);

            // Check if it's an authentication error
            if (err.response && err.response.status === 401) {
                setProfileError('Authentication failed. Please log in again.');
                return;
            }

            // If profile doesn't exist, create a default one
            const defaultProfile = {
                business_summary: {
                    executive_summary: '',
                    core_offerings: '',
                    business_model: '',
                    target_audience: '',
                    market_positioning: ''
                },
                content_strategy: {
                    pillar_content_themes: [],
                    thought_leadership_themes: [],
                    faq_content_themes: [],
                    case_study_themes: []
                },
                learning_intelligence: {
                    business_keywords: [],
                    industry_terms: [],
                    irrelevant_patterns: [],
                    search_intent_patterns: {},
                    content_themes: [],
                    topic_categories: [],
                    competitive_keywords: [],
                    audience_search_patterns: [],
                    industry_trends: [],
                    solution_keywords: []
                },
                blog_topics: {
                    primary_topics: [],
                    secondary_topics: [],
                    seasonal_topics: [],
                    trending_topics: [],
                    evergreen_topics: []
                },
                scraped_urls: [],
                distilled_context: ''
            };

            setPropertyProfile(defaultProfile);
            setEditedPropertyProfile(JSON.parse(JSON.stringify(defaultProfile)));
            setProfileError('Profile not found. You can create a new profile by filling out the form and saving.');
        } finally {
            setIsProfileLoading(false);
        }
    };

    const handleProfileSectionChange = (section) => {
        setActiveProfileSection(section);
    };

    const handleProfileUpdate = (section, subsection, value) => {
        setEditedPropertyProfile(prev => {
            const newProfile = { ...prev };
            if (subsection) {
                if (!newProfile[section]) newProfile[section] = {};
                newProfile[section][subsection] = value;
            } else {
                newProfile[section] = value;
            }
            return newProfile;
        });
    };

    // URL Management render function
    const renderURLManagement = () => (
        <Box sx={{ space: 2 }}>
            <Typography variant="h6" gutterBottom>
                URL Management
            </Typography>

            {/* Summary Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50' }}>
                        <Typography variant="h4" color="primary.main">
                            {editedPropertyProfile?.scraped_urls?.length || 0}
                        </Typography>
                        <Typography variant="body2" color="primary.main">
                            Total URLs
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                        <Typography variant="h4" color="success.main">
                            {editedPropertyProfile?.scraped_urls?.filter(url => url.status === 'accepted').length || 0}
                        </Typography>
                        <Typography variant="body2" color="success.main">
                            Accepted
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.50' }}>
                        <Typography variant="h4" color="error.main">
                            {editedPropertyProfile?.scraped_urls?.filter(url => url.status === 'rejected').length || 0}
                        </Typography>
                        <Typography variant="body2" color="error.main">
                            Rejected
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.50' }}>
                        <Typography variant="h4" color="warning.main">
                            {editedPropertyProfile?.scraped_urls?.filter(url => url.quality_score >= 0.7).length || 0}
                        </Typography>
                        <Typography variant="body2" color="warning.main">
                            High Quality
                        </Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* URL Table */}
            <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f5f5f5' }}>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    URL
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    Classification
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    Purpose
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    Quality Score
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    Status
                                </th>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #ddd' }}>
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {editedPropertyProfile?.scraped_urls?.map((urlData, index) => (
                                <tr key={index} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '12px' }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'primary.main',
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' },
                                                cursor: 'pointer',
                                                maxWidth: '200px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            onClick={() => window.open(urlData.url, '_blank')}
                                        >
                                            {urlData.url.length > 50 ? `${urlData.url.substring(0, 50)}...` : urlData.url}
                                        </Typography>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <Chip
                                            label={urlData.classification || 'other'}
                                            size="small"
                                            color="default"
                                        />
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <Chip
                                            label={urlData.purpose || 'other'}
                                            size="small"
                                            color="secondary"
                                        />
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                                                <Box
                                                    sx={{
                                                        height: '100%',
                                                        bgcolor: urlData.quality_score >= 0.7 ? 'success.main' : urlData.quality_score >= 0.4 ? 'warning.main' : 'error.main',
                                                        borderRadius: 1,
                                                        width: `${(urlData.quality_score || 0) * 100}%`
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="caption">
                                                {(urlData.quality_score || 0).toFixed(1)}
                                            </Typography>
                                        </Box>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <Chip
                                            label={urlData.status || 'accepted'}
                                            size="small"
                                            color={urlData.status === 'accepted' ? 'success' : 'error'}
                                        />
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                                const newUrls = [...editedPropertyProfile.scraped_urls];
                                                newUrls[index].status = newUrls[index].status === 'accepted' ? 'rejected' : 'accepted';
                                                handleProfileUpdate('scraped_urls', null, newUrls);
                                            }}
                                            color={urlData.status === 'accepted' ? 'error' : 'success'}
                                        >
                                            {urlData.status === 'accepted' ? 'Reject' : 'Accept'}
                                        </Button>
                                    </td>
                                </tr>
                            )) || (
                                    <tr>
                                        <td colSpan={6} style={{ padding: '24px', textAlign: 'center' }}>
                                            <Typography variant="body2" color="text.secondary">
                                                No URLs found. Create a business profile to see classified URLs.
                                            </Typography>
                                        </td>
                                    </tr>
                                )}
                        </tbody>
                    </table>
                </Box>
            </Paper>
        </Box>
    );

    const handleProfileSave = async () => {
        setIsProfileSaving(true);
        try {
            await updateBusinessProfile(selectedProperty, editedPropertyProfile);
            setSuccess('Business profile updated successfully!');
            setPropertyProfile(editedPropertyProfile);
            setShowProfileSaveModal(false);
            setPropertyProfileModalOpen(false);
        } catch (err) {
            const errMsg = err.detail || err.message || 'Failed to update business profile.';
            setError(errMsg);
        } finally {
            setIsProfileSaving(false);
        }
    };

    const handleProfileCancel = () => {
        setEditedPropertyProfile(JSON.parse(JSON.stringify(propertyProfile))); // Reset to original
        setShowProfileSaveModal(false);
    };


    // Property Settings functions
    const loadPropertySettings = async () => {
        if (!checkAuthStatus()) return;

        setIsSettingsLoading(true);
        setSettingsError('');
        try {
            // Load property data
            const propertyData = await fetchManagedPropertyById(selectedProperty);

            setProperty(propertyData);
            setPropertySettingsForm({
                friendly_name: propertyData.friendly_name || '',
                is_active_for_cycles: propertyData.is_active_for_cycles !== false,
                ga4_property_id: propertyData.ga4_property_id || '',
                ga4_property_name: propertyData.ga4_property_name || '',
                gsc_property_url: propertyData.gsc_property_url || '',
                gsc_property_name: propertyData.gsc_property_name || '',
                rewrite_config: propertyData.rewrite_config || {}
            });

            // Load WordPress configuration
            try {
                const wpConfig = await getWordPressConfig(selectedProperty);

                setWordpressConfig(wpConfig);
                setPropertySettingsForm(prev => ({
                    ...prev,
                    site_url: wpConfig.site_url || '',
                    username: wpConfig.username || '',
                    password: '', // Don't show password
                    is_enabled: wpConfig.is_enabled || false,
                    auto_publish: wpConfig.auto_publish || false,
                    default_status: wpConfig.default_status || 'publish',
                    default_category: wpConfig.default_category || '',
                    default_tags: wpConfig.default_tags || [],
                    featured_image_enabled: wpConfig.featured_image_enabled !== false,
                    seo_meta_enabled: wpConfig.seo_meta_enabled !== false,
                    auto_slug_generation: wpConfig.auto_slug_generation !== false
                }));
            } catch (wpError) {

                // Set default WordPress config
                setWordpressConfig(null);
                setPropertySettingsForm(prev => ({
                    ...prev,
                    site_url: '',
                    username: '',
                    password: '',
                    is_enabled: false,
                    auto_publish: false,
                    default_status: 'publish',
                    default_category: '',
                    default_tags: [],
                    featured_image_enabled: true,
                    seo_meta_enabled: true,
                    auto_slug_generation: true
                }));
            }
        } catch (err) {
            const errMsg = err.detail || err.message || 'Failed to load property data.';
            setSettingsError(errMsg);
        } finally {
            setIsSettingsLoading(false);
        }
    };

    const handlePropertySettingsSave = async () => {
        setIsSettingsSaving(true);
        try {
            // Only save editable fields
            const updateData = {
                friendly_name: propertySettingsForm.friendly_name,
                is_active_for_cycles: propertySettingsForm.is_active_for_cycles,
                rewrite_config: propertySettingsForm.rewrite_config
            };


            await updateManagedProperty(selectedProperty, updateData);
            setSuccess('Property settings updated successfully!');
            await loadPropertySettings(); // Reload data
        } catch (err) {
            const errMsg = err.detail || err.message || 'Failed to update property settings.';
            setError(errMsg);
        } finally {
            setIsSettingsSaving(false);
        }
    };

    const handleWordPressSettingsSave = async () => {
        setIsSettingsSaving(true);
        try {
            if (wordpressConfig) {
                // Update existing configuration
                await updateWordPressConfig(wordpressConfig.id, {
                    site_url: propertySettingsForm.site_url,
                    username: propertySettingsForm.username,
                    password: propertySettingsForm.password || undefined, // Only send if provided
                    is_enabled: propertySettingsForm.is_enabled,
                    auto_publish: propertySettingsForm.auto_publish,
                    default_status: propertySettingsForm.default_status,
                    default_category: propertySettingsForm.default_category,
                    default_tags: propertySettingsForm.default_tags,
                    featured_image_enabled: propertySettingsForm.featured_image_enabled,
                    seo_meta_enabled: propertySettingsForm.seo_meta_enabled,
                    auto_slug_generation: propertySettingsForm.auto_slug_generation
                });
            } else {
                // Create new configuration
                await createWordPressConfig({
                    managed_property_id: selectedProperty,
                    site_url: propertySettingsForm.site_url,
                    username: propertySettingsForm.username,
                    password: propertySettingsForm.password,
                    is_enabled: propertySettingsForm.is_enabled,
                    auto_publish: propertySettingsForm.auto_publish,
                    default_status: propertySettingsForm.default_status,
                    default_category: propertySettingsForm.default_category,
                    default_tags: propertySettingsForm.default_tags,
                    featured_image_enabled: propertySettingsForm.featured_image_enabled,
                    seo_meta_enabled: propertySettingsForm.seo_meta_enabled,
                    auto_slug_generation: propertySettingsForm.auto_slug_generation
                });
            }
            setSuccess('WordPress configuration updated successfully!');
            await loadPropertySettings(); // Reload data
        } catch (err) {
            const errMsg = err.detail || err.message || 'Failed to update WordPress configuration.';
            setError(errMsg);
        } finally {
            setIsSettingsSaving(false);
        }
    };


    const getStatusBadge = (status) => {
        const statusConfig = {
            'generated_pending_pickup': { label: 'Pending Review', color: 'warning' },
            'reviewed': { label: 'Reviewed', color: 'success' },
            'rejected': { label: 'Rejected', color: 'error' },
            'published': { label: 'Published', color: 'info' },
            'pending': { label: 'Pending', color: 'warning' }
        };

        const config = statusConfig[status] || { label: status, color: 'default' };
        return <Chip label={config.label} color={config.color} size="small" />;
    };

    const getContentTypeBadge = (contentType) => {
        const typeConfig = {
            'page_rewrite': { label: 'Page Rewrite', color: 'primary' },
            'blog_generation': { label: 'Blog Post', color: 'secondary' },
            'content': { label: 'Content', color: 'default' }
        };

        const config = typeConfig[contentType] || { label: contentType || 'Unknown', color: 'default' };
        return <Chip label={config.label} color={config.color} size="small" />;
    };

    if (loading && !selectedProperty) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1, sm: 3 } }}>
            {/* Alerts */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            {/* Header */}
            <Box mb={3}>
                <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2}>
                    <Box>
                        <Typography variant="h4" component="h1" gutterBottom>
                            Content Review
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Review and manage your content
                        </Typography>
                    </Box>

                    {/* Top Action Buttons */}
                    <Box display="flex" gap={1} flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={() => setExternalContentModalOpen(true)}
                            disabled={!selectedProperty}
                            size="small"
                            sx={{ flex: { xs: '1 1 auto', sm: 'none' } }}
                        >
                            {isMobile ? 'Create' : 'Create External Content'}
                        </Button>
                        {/* Temporarily hidden buttons */}
                        {false && (
                            <>
                                <Button
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={async () => {
                                        setPropertyProfileModalOpen(true);
                                        await loadPropertyProfile();
                                    }}
                                    disabled={!selectedProperty}
                                    size="small"
                                    sx={{ flex: { xs: '1 1 auto', sm: 'none' } }}
                                >
                                    {isMobile ? 'Profile' : 'View/Edit Profile'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<SettingsIcon />}
                                    onClick={() => {
                                        setPropertySettingsModalOpen(true);
                                        loadPropertySettings();
                                    }}
                                    disabled={!selectedProperty}
                                    size="small"
                                    sx={{ flex: { xs: '1 1 auto', sm: 'none' } }}
                                >
                                    {isMobile ? 'Settings' : 'Property Settings'}
                                </Button>
                            </>
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<SettingsIcon />}
                            onClick={() => {
                                setPropertySettingsModalOpen(true);
                                setActiveSettingsTab('wordpress'); // Set to WordPress tab
                                setIsWordPressOnlyMode(true); // Hide Property Settings tab
                                loadPropertySettings();
                            }}
                            disabled={!selectedProperty}
                            size="small"
                            sx={{ flex: { xs: '1 1 auto', sm: 'none' } }}
                        >
                            {isMobile ? 'WordPress' : 'WordPress Settings'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => {
                                setPropertyDetailsModalOpen(true);
                            }}
                            disabled={!selectedProperty}
                            size="small"
                            sx={{ flex: { xs: '1 1 auto', sm: 'none' } }}
                        >
                            {isMobile ? 'Details' : 'Property Details'}
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Property Selection */}
            <Box mb={3}>
                <Grid container spacing={2} alignItems="end">
                    <Grid item xs={12} md={8}>
                        <FormControl fullWidth>
                            <InputLabel>Select Property</InputLabel>
                            <Select
                                value={selectedProperty}
                                onChange={(e) => handlePropertyChange(e.target.value)}
                                label="Select Property"
                            >
                                <MenuItem value="">Select a property...</MenuItem>
                                {properties.map(property => (
                                    <MenuItem key={property.id} value={property.id}>
                                        {property.friendly_name || property.gsc_property_url}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={isRefreshing || !selectedProperty}
                            fullWidth={isMobile}
                        >
                            Refresh
                        </Button>
                    </Grid>
                </Grid>
            </Box>

            {!selectedProperty && (
                <Box textAlign="center" py={6}>
                    <Typography variant="body1" color="text.secondary">
                        Please select a property to start reviewing content
                    </Typography>
                </Box>
            )}

            {selectedProperty && (
                <>
                    {/* Bulk Actions Bar */}
                    {selectedRows.length > 0 && (
                        <Box sx={{
                            p: { xs: 1, sm: 2 },
                            mb: 2,
                            bgcolor: 'primary.light',
                            borderRadius: 1,
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            gap: 2
                        }}>
                            <Typography variant="body2">
                                {selectedRows.length} item(s) selected
                            </Typography>
                            <Box display="flex" gap={1} flexWrap="wrap" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                <Button
                                    variant="contained"
                                    color="success"
                                    size="small"
                                    onClick={() => setReviewModalOpen(true)}
                                    sx={{ flex: { xs: '1 1 auto', sm: 'none' } }}
                                >
                                    {isMobile ? 'Review' : 'Bulk Review'}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="warning"
                                    size="small"
                                    onClick={() => setRejectModalOpen(true)}
                                    sx={{ flex: { xs: '1 1 auto', sm: 'none' } }}
                                >
                                    {isMobile ? 'Reject' : 'Bulk Reject'}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    onClick={handleBulkPublish}
                                    sx={{ flex: { xs: '1 1 auto', sm: 'none' } }}
                                >
                                    {isMobile ? 'Publish' : 'Bulk Publish'}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="info"
                                    size="small"
                                    onClick={handleBulkDownload}
                                    sx={{ flex: { xs: '1 1 auto', sm: 'none' } }}
                                >
                                    {isMobile ? 'Download' : 'Bulk Download'}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    startIcon={<ImageIcon />}
                                    onClick={handleBulkGenerateImages}
                                    sx={{ flex: { xs: '1 1 auto', sm: 'none' } }}
                                >
                                    {isMobile ? 'Images' : 'Bulk Generate Images'}
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Content Display */}
                    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                        <Box sx={{ p: { xs: 1, sm: 2 }, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="h6">
                                Unreviewed Content ({content.length} items)
                            </Typography>
                        </Box>

                        {isMobile ? (
                            // Mobile view - Cards
                            <Box sx={{ p: { xs: 1, sm: 2 } }}>
                                {/* Select All Checkbox for Mobile */}
                                {content.length > 0 && (
                                    <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                                        <Checkbox
                                            checked={selectedRows.length === content.length && content.length > 0}
                                            indeterminate={selectedRows.length > 0 && selectedRows.length < content.length}
                                            onChange={handleSelectAll}
                                        />
                                        <Typography variant="body2">
                                            Select All ({selectedRows.length}/{content.length})
                                        </Typography>
                                    </Box>
                                )}
                                {content.map((item) => (
                                    <ContentCard key={item.id} item={item} />
                                ))}
                            </Box>
                        ) : (
                            // Desktop view - Table
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    checked={selectedRows.length === content.length && content.length > 0}
                                                    indeterminate={selectedRows.length > 0 && selectedRows.length < content.length}
                                                    onChange={handleSelectAll}
                                                />
                                            </TableCell>
                                            <TableCell>Content</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Created</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {content.map((item) => (
                                            <TableRow key={item.id}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox
                                                        checked={selectedRows.some(row => row.id === item.id)}
                                                        onChange={() => handleSelectRow(item)}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {item.rewritten_title || item.original_title || 'Untitled'}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {item.rewritten_meta_description || item.original_meta_description || 'No description'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {getContentTypeBadge(item.content_type || 'unknown')}
                                                </TableCell>
                                                <TableCell>
                                                    {getStatusBadge(item.status || 'pending')}
                                                </TableCell>
                                                <TableCell>
                                                    {item.created_at ? format(new Date(item.created_at), 'MMM dd, yyyy') : 'N/A'}
                                                </TableCell>
                                                <TableCell>
                                                    <Box display="flex" gap={1} flexWrap="wrap">
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => handleView(item)}
                                                            sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                                                        >
                                                            View
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="success"
                                                            onClick={() => handleReview(item)}
                                                            sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                                                        >
                                                            Review
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={() => handleReject(item)}
                                                            sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                                                        >
                                                            Reject
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="info"
                                                            onClick={() => handleDownload(item)}
                                                            sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                                                        >
                                                            Download
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="secondary"
                                                            onClick={() => handlePublish(item)}
                                                            sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                                                        >
                                                            Publish
                                                        </Button>
                                                        {!availableImages.includes(item.id) && (
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                                startIcon={<ImageIcon />}
                                                                onClick={() => handleGenerateImage(item)}
                                                                sx={{ fontSize: '0.75rem', minWidth: 'auto', px: 1 }}
                                                            >
                                                                Generate Image
                                                            </Button>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Pagination
                                count={totalPages}
                                page={currentPage}
                                onChange={(e, page) => handlePageChange(page)}
                                color="primary"
                            />
                        </Box>
                    )}
                </>
            )}

            {/* View Content Modal */}
            {viewModalOpen && selectedContent && (
                <RewrittenContentViewer
                    content={selectedContent}
                    onClose={() => setViewModalOpen(false)}
                    onReview={(content, notes) => {
                        // Check if this is an image generation message
                        if (notes && notes.includes('Image generation')) {
                            setSuccess(notes);
                        } else {
                            setSuccess('Content reviewed successfully');
                        }
                        setViewModalOpen(false);
                        fetchContent();
                    }}
                    onReject={(content) => {
                        setSuccess('Content rejected successfully');
                        setViewModalOpen(false);
                        fetchContent();
                    }}
                    hasImage={true} // Always attempt to show image; component will handle loading/errors
                    propertyId={selectedProperty}
                    isFromContentReview={true}
                />
            )}

            {/* Review Modal */}
            <Dialog open={reviewModalOpen} onClose={() => setReviewModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Review Content</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Review Notes (Optional)"
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReviewModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="success"
                        onClick={selectedContent ? handleSingleReview : handleBulkReview}
                    >
                        Approve
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Reject Modal */}
            <Dialog open={rejectModalOpen} onClose={() => setRejectModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Reject Content</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Rejection Reason"
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        margin="normal"
                        required
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={selectedContent ? handleSingleReject : handleBulkReject}
                        disabled={!rejectReason.trim()}
                    >
                        Reject
                    </Button>
                </DialogActions>
            </Dialog>

            {/* External Content Creation Modal */}
            <Dialog open={externalContentModalOpen} onClose={() => setExternalContentModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Custom Content Generation - {properties.find(p => p.id == selectedProperty)?.friendly_name}</DialogTitle>
                <DialogContent>
                    {/* Tab Navigation */}
                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Box sx={{ display: 'flex' }}>
                            <Button
                                variant={externalContentForm.activeTab === 'blog' ? 'contained' : 'text'}
                                onClick={() => setExternalContentForm({ ...externalContentForm, activeTab: 'blog' })}
                                sx={{ mr: 1 }}
                            >
                                Blog Generation
                            </Button>
                            <Button
                                variant={externalContentForm.activeTab === 'page' ? 'contained' : 'text'}
                                onClick={() => setExternalContentForm({ ...externalContentForm, activeTab: 'page' })}
                                sx={{ mr: 1 }}
                            >
                                Page Creation
                            </Button>
                            <Button
                                variant={externalContentForm.activeTab === 'image' ? 'contained' : 'text'}
                                onClick={() => setExternalContentForm({ ...externalContentForm, activeTab: 'image' })}
                            >
                                Image Generation
                            </Button>
                        </Box>
                    </Box>

                    {/* Blog Generation Form */}
                    {externalContentForm.activeTab === 'blog' && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Blog Topics (one per line)"
                                    value={externalContentForm.blogTopics}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, blogTopics: e.target.value })}
                                    placeholder="Enter blog topics, one per line&#10;Example:&#10;SEO Tips for Beginners&#10;Digital Marketing Strategies&#10;Content Optimization Guide"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Target Audience"
                                    value={externalContentForm.blogTargetAudience}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, blogTargetAudience: e.target.value })}
                                    placeholder="e.g., small business owners, digital marketers"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Desired Tone"
                                    value={externalContentForm.blogDesiredTone}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, blogDesiredTone: e.target.value })}
                                    placeholder="e.g., professional and helpful"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Quality Level</InputLabel>
                                    <Select
                                        value={externalContentForm.blogQualityLevel}
                                        onChange={(e) => setExternalContentForm({ ...externalContentForm, blogQualityLevel: e.target.value })}
                                        label="Quality Level"
                                    >
                                        <MenuItem value="low">Low</MenuItem>
                                        <MenuItem value="medium">Medium</MenuItem>
                                        <MenuItem value="high">High</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}

                    {/* Page Creation Form */}
                    {externalContentForm.activeTab === 'page' && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Page Name"
                                    value={externalContentForm.pageName}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, pageName: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Page Purpose"
                                    value={externalContentForm.pagePurpose}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, pagePurpose: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    label="Page Description"
                                    value={externalContentForm.pageDescription}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, pageDescription: e.target.value })}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Target Audience"
                                    value={externalContentForm.pageTargetAudience}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, pageTargetAudience: e.target.value })}
                                    placeholder="e.g., general web users"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Desired Tone"
                                    value={externalContentForm.pageDesiredTone}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, pageDesiredTone: e.target.value })}
                                    placeholder="e.g., informative and engaging"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Quality Level</InputLabel>
                                    <Select
                                        value={externalContentForm.pageQualityLevel}
                                        onChange={(e) => setExternalContentForm({ ...externalContentForm, pageQualityLevel: e.target.value })}
                                        label="Quality Level"
                                    >
                                        <MenuItem value="low">Low</MenuItem>
                                        <MenuItem value="medium">Medium</MenuItem>
                                        <MenuItem value="high">High</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}

                    {/* Image Generation Form */}
                    {externalContentForm.activeTab === 'image' && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Image Prompts (one per line)"
                                    value={externalContentForm.imagePrompts}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, imagePrompts: e.target.value })}
                                    placeholder="A futuristic city skyline at sunset&#10;A robot serving coffee in a modern cafe&#10;A serene mountain landscape with flowing streams"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Target Audience"
                                    value={externalContentForm.imageTargetAudience}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, imageTargetAudience: e.target.value })}
                                    placeholder="e.g., general web users, business professionals"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Desired Tone"
                                    value={externalContentForm.imageDesiredTone}
                                    onChange={(e) => setExternalContentForm({ ...externalContentForm, imageDesiredTone: e.target.value })}
                                    placeholder="e.g., professional and engaging"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Quality Level</InputLabel>
                                    <Select
                                        value={externalContentForm.imageQualityLevel}
                                        onChange={(e) => setExternalContentForm({ ...externalContentForm, imageQualityLevel: e.target.value })}
                                        label="Quality Level"
                                    >
                                        <MenuItem value="low">Low</MenuItem>
                                        <MenuItem value="medium">Medium</MenuItem>
                                        <MenuItem value="high">High</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExternalContentModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreateExternalContent}
                        disabled={
                            externalContentForm.activeTab === 'blog'
                                ? !externalContentForm.blogTopics.trim()
                                : externalContentForm.activeTab === 'page'
                                    ? !externalContentForm.pageName.trim() || !externalContentForm.pagePurpose.trim() || !externalContentForm.pageDescription.trim()
                                    : !externalContentForm.imagePrompts.trim()
                        }
                    >
                        Create {externalContentForm.activeTab === 'blog' ? 'Blog Analysis' : externalContentForm.activeTab === 'page' ? 'Page Analysis' : 'Image Generation Analysis'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Property Profile Modal */}
            <Dialog open={propertyProfileModalOpen} onClose={() => setPropertyProfileModalOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Edit Profile - {properties.find(p => p.id == selectedProperty)?.friendly_name}</DialogTitle>
                <DialogContent>
                    {isProfileLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    ) : profileError ? (
                        <Box textAlign="center" py={4}>
                            <Typography color="error">{profileError}</Typography>
                        </Box>
                    ) : !propertyProfile ? (
                        <Box textAlign="center" py={4}>
                            <Typography variant="h6">No business profile found</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Please create a business profile first.
                            </Typography>
                        </Box>
                    ) : (
                        <Box>
                            {/* Navigation Tabs */}
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                <Tabs
                                    value={activeProfileSection}
                                    onChange={(e, newValue) => handleProfileSectionChange(newValue)}
                                    variant={isMobile ? "scrollable" : "standard"}
                                    scrollButtons={isMobile ? "auto" : false}
                                    allowScrollButtonsMobile
                                    sx={{
                                        '& .MuiTab-root': {
                                            minWidth: { xs: 'auto', sm: 'auto' },
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            padding: { xs: '6px 8px', sm: '12px 16px' }
                                        }
                                    }}
                                >
                                    <Tab label={isMobile ? "Summary" : "Business Summary"} value="business_summary" />
                                    <Tab label={isMobile ? "Intelligence" : "Learning Intelligence"} value="learning_intelligence" />
                                    <Tab label={isMobile ? "Strategy" : "Content Strategy"} value="content_strategy" />
                                    <Tab label={isMobile ? "Blogs" : "Blog Topics"} value="blog_topics" />
                                    <Tab label={isMobile ? "URLs" : "URL Management"} value="url_management" />
                                </Tabs>
                            </Box>

                            {/* Content Area */}
                            <Box>
                                {activeProfileSection === 'business_summary' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Business Name"
                                                value={editedPropertyProfile?.business_summary?.business_name || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'business_name', e.target.value)}
                                                placeholder="Enter the business name..."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                label="Executive Summary"
                                                value={editedPropertyProfile?.business_summary?.executive_summary || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'executive_summary', e.target.value)}
                                                placeholder="Brief overview of the business..."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Core Offerings"
                                                value={editedPropertyProfile?.business_summary?.core_offerings || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'core_offerings', e.target.value)}
                                                placeholder="Main products/services..."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Business Model"
                                                value={editedPropertyProfile?.business_summary?.business_model || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'business_model', e.target.value)}
                                                placeholder="How the business operates..."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Target Audience"
                                                value={editedPropertyProfile?.business_summary?.target_audience || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'target_audience', e.target.value)}
                                                placeholder="Who they serve..."
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Market Positioning"
                                                value={editedPropertyProfile?.business_summary?.market_positioning || ''}
                                                onChange={(e) => handleProfileUpdate('business_summary', 'market_positioning', e.target.value)}
                                                placeholder="Competitive positioning..."
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                {activeProfileSection === 'learning_intelligence' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Business Keywords"
                                                value={editedPropertyProfile?.learning_intelligence?.business_keywords?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('learning_intelligence', 'business_keywords', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter keywords separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Industry Terms"
                                                value={editedPropertyProfile?.learning_intelligence?.industry_terms?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('learning_intelligence', 'industry_terms', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter industry terms separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Content Themes"
                                                value={editedPropertyProfile?.learning_intelligence?.content_themes?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('learning_intelligence', 'content_themes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter content themes separated by commas"
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                {activeProfileSection === 'content_strategy' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Pillar Content Themes"
                                                value={editedPropertyProfile?.content_strategy?.pillar_content_themes?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('content_strategy', 'pillar_content_themes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter pillar content themes separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Thought Leadership Themes"
                                                value={editedPropertyProfile?.content_strategy?.thought_leadership_themes?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('content_strategy', 'thought_leadership_themes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter thought leadership themes separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="FAQ Content Themes"
                                                value={editedPropertyProfile?.content_strategy?.faq_content_themes?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('content_strategy', 'faq_content_themes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter FAQ content themes separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Case Study Themes"
                                                value={editedPropertyProfile?.content_strategy?.case_study_themes?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('content_strategy', 'case_study_themes', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter case study themes separated by commas"
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                {activeProfileSection === 'blog_topics' && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Primary Topics"
                                                value={editedPropertyProfile?.blog_topics?.primary_topics?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('blog_topics', 'primary_topics', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter primary blog topics separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Secondary Topics"
                                                value={editedPropertyProfile?.blog_topics?.secondary_topics?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('blog_topics', 'secondary_topics', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter secondary blog topics separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Seasonal Topics"
                                                value={editedPropertyProfile?.blog_topics?.seasonal_topics?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('blog_topics', 'seasonal_topics', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter seasonal blog topics separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Trending Topics"
                                                value={editedPropertyProfile?.blog_topics?.trending_topics?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('blog_topics', 'trending_topics', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter trending blog topics separated by commas"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                label="Evergreen Topics"
                                                value={editedPropertyProfile?.blog_topics?.evergreen_topics?.join(', ') || ''}
                                                onChange={(e) => handleProfileUpdate('blog_topics', 'evergreen_topics', e.target.value.split(',').map(s => s.trim()).filter(s => s))}
                                                placeholder="Enter evergreen blog topics separated by commas"
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                                {activeProfileSection === 'url_management' && (
                                    <Box>
                                        {renderURLManagement()}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPropertyProfileModalOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setShowProfileSaveModal(true)}
                        disabled={isProfileSaving}
                    >
                        {isProfileSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Save Confirmation Modal */}
            <Dialog open={showProfileSaveModal} onClose={handleProfileCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Save Changes</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to save the changes to your business profile? This will update the profile used by the learning system.
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <AlertTitle>Note</AlertTitle>
                        Changes to the business profile will affect how the learning system filters and analyzes content for this property.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleProfileCancel}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleProfileSave}
                        disabled={isProfileSaving}
                    >
                        {isProfileSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Property Settings Modal */}
            <Dialog open={propertySettingsModalOpen} onClose={() => {
                setPropertySettingsModalOpen(false);
                setIsWordPressOnlyMode(false); // Reset WordPress-only mode
                setActiveSettingsTab('property'); // Reset to default tab
            }} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Property Settings: {properties.find(p => p.id == selectedProperty)?.friendly_name}
                        </Typography>
                        <IconButton onClick={() => {
                            setPropertySettingsModalOpen(false);
                            setIsWordPressOnlyMode(false); // Reset WordPress-only mode
                            setActiveSettingsTab('property'); // Reset to default tab
                        }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {isSettingsLoading ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    ) : settingsError ? (
                        <Box textAlign="center" py={4}>
                            <Typography color="error">{settingsError}</Typography>
                        </Box>
                    ) : (
                        <Box>
                            {/* Tab Navigation */}
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                <Tabs
                                    value={activeSettingsTab}
                                    onChange={(e, newValue) => setActiveSettingsTab(newValue)}
                                    variant={isMobile ? "scrollable" : "standard"}
                                    scrollButtons={isMobile ? "auto" : false}
                                    allowScrollButtonsMobile
                                    sx={{
                                        '& .MuiTab-root': {
                                            minWidth: { xs: 'auto', sm: 'auto' },
                                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                            padding: { xs: '6px 8px', sm: '12px 16px' }
                                        }
                                    }}
                                >
                                    {!isWordPressOnlyMode && (
                                        <Tab label={isMobile ? "Property" : "Property Settings"} value="property" />
                                    )}
                                    <Tab label={isMobile ? "WordPress" : "WordPress Configuration"} value="wordpress" />
                                </Tabs>
                            </Box>

                            {/* Property Settings Tab */}
                            {activeSettingsTab === 'property' && (
                                <Box sx={{ space: 3 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Friendly Name"
                                                value={propertySettingsForm.friendly_name || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, friendly_name: e.target.value })}
                                                placeholder="Enter a friendly name for this property"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="GA4 Property ID"
                                                value={propertySettingsForm.ga4_property_id || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, ga4_property_id: e.target.value })}
                                                placeholder="GA4 Property ID"
                                                InputProps={{ readOnly: true }}
                                                helperText="Google Analytics 4 Property ID (read-only)"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="GA4 Property Name"
                                                value={propertySettingsForm.ga4_property_name || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, ga4_property_name: e.target.value })}
                                                placeholder="GA4 Property Name"
                                                InputProps={{ readOnly: true }}
                                                helperText="Google Analytics 4 Property Name (read-only)"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="GSC Property URL"
                                                value={propertySettingsForm.gsc_property_url || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, gsc_property_url: e.target.value })}
                                                placeholder="https://example.com"
                                                InputProps={{ readOnly: true }}
                                                helperText="Google Search Console Property URL (read-only)"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="GSC Property Name"
                                                value={propertySettingsForm.gsc_property_name || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, gsc_property_name: e.target.value })}
                                                placeholder="GSC Property Name"
                                                InputProps={{ readOnly: true }}
                                                helperText="Google Search Console Property Name (read-only)"
                                            />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <FormControl fullWidth>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.is_active_for_cycles || false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, is_active_for_cycles: e.target.checked })}
                                                    />
                                                    <Typography>Active for Content Cycles</Typography>
                                                </Box>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                Note: GA4 and GSC properties are read-only as they are managed by the system.
                                                Only the friendly name and active status can be modified.
                                            </Typography>
                                        </Grid>

                                        {/* Rewrite Configuration Section */}
                                        <Grid item xs={12}>
                                            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
                                                Content Rewrite Configuration
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Content Strategy</InputLabel>
                                                <Select
                                                    value={propertySettingsForm.rewrite_config?.user_config?.content_strategy || 'dynamic'}
                                                    onChange={(e) => setPropertySettingsForm({
                                                        ...propertySettingsForm,
                                                        rewrite_config: {
                                                            ...propertySettingsForm.rewrite_config,
                                                            user_config: {
                                                                ...propertySettingsForm.rewrite_config?.user_config,
                                                                content_strategy: e.target.value
                                                            }
                                                        }
                                                    })}
                                                    label="Content Strategy"
                                                >
                                                    <MenuItem value="dynamic">Dynamic</MenuItem>
                                                    <MenuItem value="balanced">Balanced</MenuItem>
                                                    <MenuItem value="pages_only">Pages Only</MenuItem>
                                                    <MenuItem value="blogs_only">Blogs Only</MenuItem>
                                                    <MenuItem value="mixed">Mixed</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Total Content Per Cycle"
                                                type="number"
                                                value={propertySettingsForm.rewrite_config?.user_config?.total_content_per_cycle || 10}
                                                onChange={(e) => setPropertySettingsForm({
                                                    ...propertySettingsForm,
                                                    rewrite_config: {
                                                        ...propertySettingsForm.rewrite_config,
                                                        user_config: {
                                                            ...propertySettingsForm.rewrite_config?.user_config,
                                                            total_content_per_cycle: parseInt(e.target.value)
                                                        }
                                                    }
                                                })}
                                                inputProps={{ min: 1, max: 50 }}
                                                helperText="Number of content pieces to generate per cycle"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Max Pages Per Cycle"
                                                type="number"
                                                value={propertySettingsForm.rewrite_config?.user_config?.max_pages_per_cycle || 6}
                                                onChange={(e) => setPropertySettingsForm({
                                                    ...propertySettingsForm,
                                                    rewrite_config: {
                                                        ...propertySettingsForm.rewrite_config,
                                                        user_config: {
                                                            ...propertySettingsForm.rewrite_config?.user_config,
                                                            max_pages_per_cycle: parseInt(e.target.value)
                                                        }
                                                    }
                                                })}
                                                inputProps={{ min: 0, max: 20 }}
                                                helperText="Maximum pages to rewrite per cycle"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Max Blogs Per Cycle"
                                                type="number"
                                                value={propertySettingsForm.rewrite_config?.user_config?.max_blogs_per_cycle || 4}
                                                onChange={(e) => setPropertySettingsForm({
                                                    ...propertySettingsForm,
                                                    rewrite_config: {
                                                        ...propertySettingsForm.rewrite_config,
                                                        user_config: {
                                                            ...propertySettingsForm.rewrite_config?.user_config,
                                                            max_blogs_per_cycle: parseInt(e.target.value)
                                                        }
                                                    }
                                                })}
                                                inputProps={{ min: 0, max: 20 }}
                                                helperText="Maximum blog posts to generate per cycle"
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Quality Level</InputLabel>
                                                <Select
                                                    value={propertySettingsForm.rewrite_config?.user_config?.quality_level || 'medium'}
                                                    onChange={(e) => setPropertySettingsForm({
                                                        ...propertySettingsForm,
                                                        rewrite_config: {
                                                            ...propertySettingsForm.rewrite_config,
                                                            user_config: {
                                                                ...propertySettingsForm.rewrite_config?.user_config,
                                                                quality_level: e.target.value
                                                            }
                                                        }
                                                    })}
                                                    label="Quality Level"
                                                >
                                                    <MenuItem value="low">Low</MenuItem>
                                                    <MenuItem value="medium">Medium</MenuItem>
                                                    <MenuItem value="high">High</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Business Focus</InputLabel>
                                                <Select
                                                    value={propertySettingsForm.rewrite_config?.user_config?.business_focus || 'balanced'}
                                                    onChange={(e) => setPropertySettingsForm({
                                                        ...propertySettingsForm,
                                                        rewrite_config: {
                                                            ...propertySettingsForm.rewrite_config,
                                                            user_config: {
                                                                ...propertySettingsForm.rewrite_config?.user_config,
                                                                business_focus: e.target.value
                                                            }
                                                        }
                                                    })}
                                                    label="Business Focus"
                                                >
                                                    <MenuItem value="balanced">Balanced</MenuItem>
                                                    <MenuItem value="seo_uplift">SEO Uplift</MenuItem>
                                                    <MenuItem value="engagement_improvement">Engagement Improvement</MenuItem>
                                                    <MenuItem value="conversion_optimization">Conversion Optimization</MenuItem>
                                                    <MenuItem value="traffic_growth">Traffic Growth</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Content Update Frequency</InputLabel>
                                                <Select
                                                    value={propertySettingsForm.rewrite_config?.user_config?.content_update_frequency || 'monthly'}
                                                    onChange={(e) => setPropertySettingsForm({
                                                        ...propertySettingsForm,
                                                        rewrite_config: {
                                                            ...propertySettingsForm.rewrite_config,
                                                            user_config: {
                                                                ...propertySettingsForm.rewrite_config?.user_config,
                                                                content_update_frequency: e.target.value
                                                            }
                                                        }
                                                    })}
                                                    label="Content Update Frequency"
                                                >
                                                    <MenuItem value="weekly">Weekly</MenuItem>
                                                    <MenuItem value="biweekly">Bi-weekly</MenuItem>
                                                    <MenuItem value="monthly">Monthly</MenuItem>
                                                    <MenuItem value="quarterly">Quarterly</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                These settings control how content is generated and optimized for your property.
                                                Changes will take effect on the next content cycle.
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Box>
                            )}

                            {/* WordPress Configuration Tab */}
                            {activeSettingsTab === 'wordpress' && (
                                <Box sx={{ space: 3 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="WordPress Site URL"
                                                type="url"
                                                value={propertySettingsForm.site_url || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, site_url: e.target.value })}
                                                placeholder="https://example.com"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Username"
                                                value={propertySettingsForm.username || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, username: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Password"
                                                type="password"
                                                value={propertySettingsForm.password || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, password: e.target.value })}
                                                placeholder={wordpressConfig ? '••••••••' : 'Enter password'}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel>Default Post Status</InputLabel>
                                                <Select
                                                    value={propertySettingsForm.default_status || 'publish'}
                                                    onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, default_status: e.target.value })}
                                                    label="Default Post Status"
                                                >
                                                    <MenuItem value="publish">Publish</MenuItem>
                                                    <MenuItem value="draft">Draft</MenuItem>
                                                    <MenuItem value="private">Private</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Default Category"
                                                value={propertySettingsForm.default_category || ''}
                                                onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, default_category: e.target.value })}
                                                placeholder="e.g., General"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Default Tags (comma-separated)"
                                                value={Array.isArray(propertySettingsForm.default_tags) ? propertySettingsForm.default_tags.join(', ') : ''}
                                                onChange={(e) => setPropertySettingsForm({
                                                    ...propertySettingsForm,
                                                    default_tags: e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag)
                                                })}
                                                placeholder="tag1, tag2, tag3"
                                            />
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ mt: 4 }}>
                                        <Typography variant="h6" gutterBottom>Publishing Options</Typography>
                                        <Stack spacing={2}>
                                            <FormControl>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.is_enabled || false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, is_enabled: e.target.checked })}
                                                    />
                                                    <Typography>Enable WordPress Publishing</Typography>
                                                </Box>
                                            </FormControl>
                                            <FormControl>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.auto_publish || false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, auto_publish: e.target.checked })}
                                                    />
                                                    <Typography>Auto-publish content without review</Typography>
                                                </Box>
                                            </FormControl>
                                            <FormControl>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.featured_image_enabled !== false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, featured_image_enabled: e.target.checked })}
                                                    />
                                                    <Typography>Enable featured image support</Typography>
                                                </Box>
                                            </FormControl>
                                            <FormControl>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.seo_meta_enabled !== false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, seo_meta_enabled: e.target.checked })}
                                                    />
                                                    <Typography>Enable SEO meta fields</Typography>
                                                </Box>
                                            </FormControl>
                                            <FormControl>
                                                <Box display="flex" alignItems="center">
                                                    <Checkbox
                                                        checked={propertySettingsForm.auto_slug_generation !== false}
                                                        onChange={(e) => setPropertySettingsForm({ ...propertySettingsForm, auto_slug_generation: e.target.checked })}
                                                    />
                                                    <Typography>Auto-generate slugs from titles</Typography>
                                                </Box>
                                            </FormControl>
                                        </Stack>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPropertySettingsModalOpen(false)}>Cancel</Button>
                    {activeSettingsTab === 'property' ? (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handlePropertySettingsSave}
                            disabled={isSettingsSaving}
                        >
                            {isSettingsSaving ? 'Saving...' : 'Save Property Settings'}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleWordPressSettingsSave}
                            disabled={isSettingsSaving}
                        >
                            {isSettingsSaving ? 'Saving...' : 'Save WordPress Settings'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>


            {/* Property Details Modal */}
            <Dialog open={propertyDetailsModalOpen} onClose={() => setPropertyDetailsModalOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Property Details - {properties.find(p => p.id == selectedProperty)?.friendly_name}</DialogTitle>
                <DialogContent>
                    {selectedProperty && properties.find(p => p.id == selectedProperty) ? (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>Property Information</Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Property ID"
                                    value={selectedProperty}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Friendly Name"
                                    value={properties.find(p => p.id == selectedProperty)?.friendly_name || ''}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="GSC Property URL"
                                    value={properties.find(p => p.id == selectedProperty)?.gsc_property_url || ''}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="GA4 Property ID"
                                    value={properties.find(p => p.id == selectedProperty)?.ga4_property_id || ''}
                                    InputProps={{ readOnly: true }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Content Statistics</Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center' }}>
                                    <Typography variant="h4" color="primary">
                                        {content.length}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total Content Items
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center' }}>
                                    <Typography variant="h4" color="success.main">
                                        {content.filter(item => item.status === 'reviewed').length}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Reviewed Items
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: { xs: 1, sm: 2 }, textAlign: 'center' }}>
                                    <Typography variant="h4" color="warning.main">
                                        {content.filter(item => item.status === 'generated_pending_pickup').length}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Pending Review
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    ) : (
                        <Box textAlign="center" py={4}>
                            <Typography>No property selected</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPropertyDetailsModalOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CASDashboardPage; 